import { Card } from '@/components/ui/card'
import {
  formatPercent,
  formatUnits,
  formatWaveDate,
  weekLabel,
  type WavePlanningFixture,
  type calculateWavePlanning,
} from './model'

interface Props {
  fixture: WavePlanningFixture
  result: ReturnType<typeof calculateWavePlanning>
  year: number
  setDate: string
}

export function WavePlanningSummary({ fixture, result, year, setDate }: Props) {
  const fields = [
    [`${year} stores`, formatUnits(fixture.stores)],
    [`${year} SKUs`, formatUnits(fixture.skus)],
    [
      `${year - 1} stores / SKUs`,
      `${fixture.previousStores === null ? 'N/A' : formatUnits(fixture.previousStores)} / ${fixture.previousSkus === null ? 'N/A' : formatUnits(fixture.previousSkus)}`,
    ],
    [
      'POG set / max qty',
      `${formatUnits(fixture.pogSet)} / ${fixture.pogMax === null ? '—' : formatUnits(fixture.pogMax)}`,
    ],
    [
      'Min / max units',
      `${formatUnits(fixture.pogSet * fixture.stores)} / ${formatUnits((fixture.pogMax ?? 0) * fixture.stores)}`,
    ],
    ['In-season D/T', formatPercent(result.inSeasonDemandPercent, 1)],
    [
      'Avg store receipts / sales',
      `${formatUnits(result.averageStoreReceipts)} / ${formatUnits(result.averageStoreSales)}`,
    ],
    ['Set date · Friday', `${formatWaveDate(setDate)}, ${year}`],
  ]

  return (
    <Card className="min-w-0 p-5 shadow-none">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Planning reference</h2>
        <span className="text-xs text-muted-foreground">
          {fixture.season} · {weekLabel(fixture.setWeek)} / SEA{fixture.setWeek}
        </span>
      </div>
      <div className="my-4 grid grid-cols-2 divide-x border-b pb-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Planned purchase units
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {formatUnits(fixture.purchaseUnits)}
          </p>
        </div>
        <div className="pl-5">
          <p className="text-xs text-muted-foreground">Minimum first wave</p>
          <p
            className="mt-1 text-2xl font-semibold tabular-nums"
            title={formatPercent(result.minimumFirstWave, 3)}
          >
            {formatPercent(result.minimumFirstWave, 1)}
          </p>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-5 gap-y-2.5">
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 text-xs font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {fixture.note && (
        <p className="mt-3 border-t pt-3 text-[11px] leading-relaxed text-muted-foreground">
          {fixture.note}
        </p>
      )}
    </Card>
  )
}
